import {
    current,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

import { SetCourseExpandCollapse } from '../../../data-grid-container/courses/types/courses.types';
import {
    manageCourseStrandIsExpanded,
    manageCourseTopicExpandCollapseCheck,
    manageStrandExpandCollapseIconClick
} from '../../helpers/courses/course-expand-helpers';
import {
    GridCells,
    GridCounters,
    ToggleActivityGridCheckbox,
    ToggleGridCell,
    ToggleStudentGridCheckbox,
    ToggleTopicGridCheckbox
} from '../../helpers/slice/topic/topic-slice.types';
import { ChallengesConstants, ChallengesState } from './challenges.types';
import { RootState } from '@config/react-redux/store';
import { DynamicParams } from '@config/react-router-dom/hooks/query-params.types';
import {
    SetCardViewSelectPayload
} from '@containers/activity-router/data-grid-container/dynamic-grid-filters/components/card-view/card-view.types';
import { StudentListData } from '@containers/activity-router/data-grid-container/student-list/student-list.types';
import { resetGridCells } from '@containers/activity-router/routes/helpers/grid/cells/grid-cells-helpers';
import {
    resetGridCellCounters
} from '@containers/activity-router/routes/helpers/grid/counters/grid-cell-counters/grid-cell-counters';
import { RouteHelpersModalConstants } from '@containers/activity-router/routes/helpers/modal/route-modal-helpers.types';
import { getMultiSelectDraftCopy } from '@containers/activity-router/routes/helpers/multi-select/multi-select-helpers';
import { RouteMultiSelectPayload } from '@containers/activity-router/routes/helpers/multi-select/multi-select-helpers.types';
import {
    getUpdatedFilteredGridData,
    toggleGridCells
} from '@containers/activity-router/routes/helpers/slice/grid/slice-grid-helpers';
import { defaultState } from '@containers/activity-router/routes/helpers/slice/slice-defaults';
import {
    toggleRouteActivityGridCheckbox,
    toggleRouteStudentGridCheckbox,
    toggleRouteTopicGridCheckbox
} from '@containers/activity-router/routes/helpers/slice/slice-helpers';
import {
    manageChallengesMultiSelect
} from '@containers/activity-router/routes/learn/challenges/components/challenges-multi-select/challenges-multi-select-helpers';
import {
    updateDokFilterByValue
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/dok-filter/dok-filter-helpers';
import {
    SetChallengesFilterDokPayload
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/dok-filter/dok-filter.types';
import {
    sortFilterLevels
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/level-filter/level-filter-helpers';
import {
    ChallengesFilterLevel,
    SetChallengesFilterLevelPayload
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/level-filter/level-filter.types';
import {
    getChallengesPlaylistLevelAndDok
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/playlist-filter/playlist-filter-helpers';
import {
    ChallengesPlaylist,
    DEFAULT_PLAYLIST_ID
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/playlist-filter/playlist-filter.types';
import {
    manageChallengesCellAssignmentsSelected,
    manageChallengesSelectedAssignments,
    updateChallengesAssignments
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/assignments/selected/challenges-assignments-selected-helpers';
import {
    generateGridFiltersFromCardView, selectCardViewItems
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/card-view/card-view-helpers';
import {
    getChallengesFilteredCourse,
    getChallengesSelectableCourse
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/course/get-challenges-filtered-course';
import {
    getChallengesIsCellVisible
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/grid/get-challenges-is-cell-visible';
import {
    TransformCourse,
    TransformedChallengesAssignments
} from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';

const { name } = ChallengesConstants;
const getIsCellVisible = (state: ChallengesState) => getChallengesIsCellVisible({
    filteredCourse: state.course.filtered,
    filters: state.filters
});
const resetChallengesStrandTopicFilters = (state: ChallengesState) => {
    state.filters.strand = [];
    state.filters.topic = [];
};
const initialState: ChallengesState = {
    ...defaultState,
    course: {
        selected: null,
        filtered: null
    },
    filters: {
        level: [],
        dok: [],
        strand: [],
        topic: [],
        playlist: {
            id: DEFAULT_PLAYLIST_ID,
            activityIds: []
        },
        isExpanded: true,
        dynamicParams: []
    },
    assignments: {
        ...defaultState.assignments,
        grid: {}
    },
    modal: null
};

export const challengesSlice = createSlice({
    name,
    initialState,
    reducers: {
        setChallengesCourseSelected (state, action: PayloadAction<TransformCourse>) {
            state.course.selected = action.payload;
            state.course.filtered = action.payload;
        },
        setChallengesCourseIsExpanded (state, action: PayloadAction<SetCourseExpandCollapse>) {
            if (state.course.selected) {
                state.course.selected.topics = manageCourseStrandIsExpanded({
                    course: state.course.selected,
                    payload: action.payload
                });
            }

            if (state.course.filtered) {
                state.course.filtered.topics = manageCourseStrandIsExpanded({
                    course: state.course.filtered,
                    payload: action.payload
                });
            }

            if (state.course.filtered && state.course.selected) {
                if (manageCourseTopicExpandCollapseCheck({
                    course: state.course.filtered || state.course.selected,
                    isExpanded: state.filters.isExpanded
                })) state.filters.isExpanded = !state.filters.isExpanded;
            }
        },
        setChallengesGridCells (state, action: PayloadAction<GridCells>) {
            state.grid.cells = action.payload;
        },
        setChallengesGridCountersInitialValue (state, action: PayloadAction<GridCounters>) {
            state.grid.counters = action.payload;
        },
        toggleChallengesGridCells (state, action: PayloadAction<ToggleGridCell>) {
            const {
                student,
                ...payload
            } = action.payload;
            const {
                gridCells,
                gridCounters
            } = toggleGridCells({
                gridCells: state.grid.cells,
                gridCounters: state.grid.counters,
                studentId: student.userProfileId.toString(),
                ...payload
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;

            state.assignments.selected = manageChallengesCellAssignmentsSelected({
                ...action.payload,
                studentAssignments: state.assignments.grid,
                gridCells: state.grid.cells,
                selectedAssignments: state.assignments.selected
            });
        },
        toggleChallengesStudentGridCheckbox (state, action: PayloadAction<ToggleStudentGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteStudentGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageChallengesSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                }),
                isCellVisible: getIsCellVisible(state)
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        toggleChallengesTopicGridCheckbox (state, action: PayloadAction<ToggleTopicGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteTopicGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageChallengesSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                }),
                isCellVisible: getIsCellVisible(state)
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        toggleChallengesActivityGridCheckbox (state, action: PayloadAction<ToggleActivityGridCheckbox>) {
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = toggleRouteActivityGridCheckbox({
                payload: action.payload,
                state: {
                    gridCells: state.grid.cells,
                    gridCounters: state.grid.counters,
                    assignmentsSelected: state.assignments.selected
                },
                manageSelectedAssignments: (params) => manageChallengesSelectedAssignments({
                    ...params,
                    studentAssignments: state.assignments.grid
                })
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
        },
        setChallengesAssignmentGrid (state, action: PayloadAction<TransformedChallengesAssignments>) {
            state.assignments.grid = action.payload;
        },
        resetChallengesAssignments (state) {
            state.assignments = {
                grid: {},
                selected: defaultState.assignments.selected
            };
        },
        resetChallengesGrid (state) {
            state.grid.cells = resetGridCells({
                gridCells: state.grid.cells,
                isSelected: false
            });
            state.grid.counters = resetGridCellCounters({
                counters: state.grid.counters
            });
        },
        resetChallengesSelectedAssignments (state) {
            state.assignments.selected = defaultState.assignments.selected;
        },
        setChallengesModal (state, action: PayloadAction<RouteHelpersModalConstants | null>) {
            state.modal = action.payload;
        },
        updateChallengesAssignmentSelected (state, action: PayloadAction<TransformedChallengesAssignments>) {
            state.assignments.selected = updateChallengesAssignments({
                studentAssignments: action.payload,
                selectedAssignments: state.assignments.selected,
                gridCells: state.grid.cells
            });
        },
        setChallengesFilterLevel (state, { payload }: PayloadAction<SetChallengesFilterLevelPayload>) {
            let updatedLevelFilter: ChallengesFilterLevel;

            if (payload === null) {
                updatedLevelFilter = [];
            } else if (Array.isArray(payload)) {
                updatedLevelFilter = sortFilterLevels(payload);
            } else {
                const set = new Set(state.filters.level);

                set.has(payload) ? set.delete(payload) : set.add(payload);
                updatedLevelFilter = sortFilterLevels([...set]);
            }

            state.filters.level = updatedLevelFilter;
            state.filters.dok = [];
            resetChallengesStrandTopicFilters(state);
        },
        resetChallengesFilterLevel (state, action: PayloadAction<ChallengesFilterLevel>) {
            state.filters.level = action.payload;
        },
        setChallengesFilterStrandTopic (state, action: PayloadAction<{ strand: string[]; topic: string[] }>) {
            state.filters.strand = action.payload.strand;
            state.filters.topic = action.payload.topic;
        },
        setChallengesFilterDok (state, action: PayloadAction<SetChallengesFilterDokPayload>) {
            const updatedDokValue = action.payload;
            const currentDokFilter = state.filters.dok;

            state.filters.dok = updateDokFilterByValue({
                updatedDokValue,
                currentDokFilter
            });
            resetChallengesStrandTopicFilters(state);
        },
        setChallengesPlaylistFilter (state, action: PayloadAction<ChallengesPlaylist>) {
            const isDefaultPlaylist = action.payload.id === DEFAULT_PLAYLIST_ID;
            const { updatedCourse } = getChallengesFilteredCourse({
                course: state.course.selected,
                filterLevel: state.filters.level,
                filterDok: state.filters.dok,
                filterStrand: [],
                filterTopic: [],
                filterPlaylist: action.payload
            });

            if (!isDefaultPlaylist && updatedCourse) {
                const {
                    updatedLevelFilter,
                    updatedDokFilter
                } = getChallengesPlaylistLevelAndDok(updatedCourse);

                state.filters.level = updatedLevelFilter;
                state.filters.dok = updatedDokFilter;
            }

            state.filters.playlist = action.payload;
            resetChallengesStrandTopicFilters(state);
        },
        setChallengesCardViewSelect (state, action: PayloadAction<SetCardViewSelectPayload>) {
            const {
                selectedItems,
                studentListData,
                instructionHubBlockedKeys
            } = action.payload;
            const {
                updatedLevelFilter,
                updatedDokFilter,
                updatedPlaylistFilter,
                updatedStrandFilter,
                updatedTopicFilter
            } = generateGridFiltersFromCardView({
                course: state.course.selected as TransformCourse,
                selectedItems,
                assignmentsSelected: state.assignments.selected,
                playlistGridFilter: state.filters.playlist,
                filterStrand: state.filters.strand,
                filterTopic: state.filters.topic
            });
            const { updatedCourse } = getChallengesFilteredCourse({
                course: state.course.selected,
                filterLevel: updatedLevelFilter,
                filterDok: updatedDokFilter,
                filterStrand: updatedStrandFilter,
                filterTopic: updatedTopicFilter,
                filterPlaylist: updatedPlaylistFilter
            });
            const { updatedCourse: selectableCourse } = getChallengesSelectableCourse({
                course: state.course.selected,
                filterLevel: updatedLevelFilter,
                filterPlaylist: updatedPlaylistFilter
            });
            const {
                updatedGridCells,
                updatedGridCounters,
                updatedSelectedAssignments
            } = getUpdatedFilteredGridData({
                prevGridCells: { ...state.grid.cells },
                studentListData,
                updatedFilteredCourse: selectableCourse,
                manageSelectedAssignmentsCallback: manageChallengesSelectedAssignments,
                studentAssignments: state.assignments.grid
            });
            const {
                gridCells,
                gridCounters,
                assignmentsSelected
            } = selectCardViewItems({
                selectedItems,
                gridCells: updatedGridCells,
                gridCounters: updatedGridCounters,
                assignmentsSelected: updatedSelectedAssignments,
                assignmentsGrid: state.assignments.grid,
                instructionHubBlockedKeys
            });

            state.course.filtered = updatedCourse;
            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = assignmentsSelected;
            state.filters.level = updatedLevelFilter;
            state.filters.dok = updatedDokFilter;
            state.filters.playlist = updatedPlaylistFilter;
            state.filters.strand = updatedStrandFilter;
            state.filters.topic = updatedTopicFilter;
        },
        setChallengesFilteredGridData (state, action: PayloadAction<{ studentListData: StudentListData }>) {
            const { studentListData } = action.payload;
            const { updatedCourse } = getChallengesFilteredCourse({
                course: state.course.selected,
                filterLevel: state.filters.level,
                filterDok: state.filters.dok,
                filterStrand: state.filters.strand,
                filterTopic: state.filters.topic,
                filterPlaylist: state.filters.playlist
            });
            const { updatedCourse: selectableCourse } = getChallengesSelectableCourse({
                course: state.course.selected,
                filterLevel: state.filters.level,
                filterPlaylist: state.filters.playlist
            });
            const {
                updatedGridCells,
                updatedGridCounters,
                updatedSelectedAssignments
            } = getUpdatedFilteredGridData({
                prevGridCells: { ...state.grid.cells },
                studentListData,
                updatedFilteredCourse: selectableCourse,
                manageSelectedAssignmentsCallback: manageChallengesSelectedAssignments,
                studentAssignments: state.assignments.grid
            });

            state.course.filtered = updatedCourse;
            state.grid.cells = updatedGridCells;
            state.grid.counters = updatedGridCounters;
            state.assignments.selected = updatedSelectedAssignments;
        },
        setChallengesIsExpanded (state, action: PayloadAction<boolean>) {
            const isExpanded = action.payload;

            state.filters.isExpanded = isExpanded;

            if (state.course.selected) {
                state.course.selected = manageStrandExpandCollapseIconClick({
                    course: state.course.selected,
                    isExpanded
                });
                if (state.course.filtered) {
                    state.course.filtered = manageStrandExpandCollapseIconClick({
                        course: state.course.filtered,
                        isExpanded
                    });
                }
            }
        },
        setChallengesMultiSelect (state, action: PayloadAction<RouteMultiSelectPayload>) {
            const {
                gridCells,
                gridCounters,
                selectedAssignments
            } = manageChallengesMultiSelect({
                option: action.payload.option,
                instructionHubBlockedKeys: action.payload.instructionHubBlockedKeys,
                gridCells: getMultiSelectDraftCopy(state.grid.cells),
                gridCounters: getMultiSelectDraftCopy(state.grid.counters),
                studentAssignments: current(state.assignments.grid),
                selectedAssignments: getMultiSelectDraftCopy(state.assignments.selected),
                isCellVisible: getIsCellVisible(state)
            });

            state.grid.cells = gridCells;
            state.grid.counters = gridCounters;
            state.assignments.selected = selectedAssignments;
        },
        setChallengesDynamicParams (state, action: PayloadAction<{ option: DynamicParams }>) {
            const { option } = action.payload;

            if (!state.filters.dynamicParams.includes(option)) {
                state.filters.dynamicParams.push(option);
            }
        }
    }
});

/**
 * ACTIONS
 */
export const {
    setChallengesCourseSelected,
    setChallengesCourseIsExpanded,
    setChallengesGridCells,
    toggleChallengesStudentGridCheckbox,
    setChallengesGridCountersInitialValue,
    toggleChallengesGridCells,
    toggleChallengesTopicGridCheckbox,
    toggleChallengesActivityGridCheckbox,
    setChallengesAssignmentGrid,
    resetChallengesAssignments,
    resetChallengesGrid,
    resetChallengesSelectedAssignments,
    setChallengesModal,
    updateChallengesAssignmentSelected,
    setChallengesFilterLevel,
    resetChallengesFilterLevel,
    setChallengesFilterStrandTopic,
    setChallengesFilterDok,
    setChallengesIsExpanded,
    setChallengesCardViewSelect,
    setChallengesFilteredGridData,
    setChallengesMultiSelect,
    setChallengesDynamicParams,
    setChallengesPlaylistFilter
} = challengesSlice.actions;

/**
 * SELECTORS
 */
export const selectChallengesCourseSelected = (state: RootState) => state[name].course.selected;

export const selectChallengesCourseFiltered = (state: RootState) => state[name].course.filtered;

export const selectChallengesGridCounters = (state: RootState) => state[name].grid.counters;

export const selectChallengesGridCells = (state: RootState) => state[name].grid.cells;

export const selectChallengesAssignmentsGrid = (state: RootState) => state[name].assignments.grid;

export const selectChallengesAssignmentsSelected = (state: RootState) => state[name].assignments.selected;

export const selectChallengesModal = (state: RootState) => state[name].modal;

export const selectChallengesFilterLevel = (state: RootState) => state[name].filters.level;

export const selectChallengesFilterDok = (state: RootState) => state[name].filters.dok;

export const selectChallengesFilterStrand = (state: RootState) => state[name].filters.strand;

export const selectChallengesFilterTopic = (state: RootState) => state[name].filters.topic;

export const selectChallengesIsExpanded = (state: RootState) => state[name].filters.isExpanded;

export const selectChallengesDynamicParams = (state: RootState) => state[name].filters.dynamicParams;

export const selectChallengesPlaylistFilter = (state: RootState) => state[name].filters.playlist;

export default challengesSlice.reducer;
